import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { toast } from "@/shared/hooks/use-toast";
import { uploadFileToSignedUrl } from "@/shared/lib/uploadSignedUrl";

import { templatesApi } from "../api";
import type { AssetContentType, TemplateNameTranslation, TemplateRecord } from "../api";
import type { Template } from "../types";
import { templateKeys } from "./template.keys";

export type SaveStep =
  | { phase: "idle" }
  | { phase: "creating" }
  | { phase: "uploading"; current: number; total: number; fileName: string }
  | { phase: "saving" }
  | { phase: "done" };

export interface SaveTemplateInput {
  existingId?: string;
  template: Template;
  nameTranslations: TemplateNameTranslation[];
  preview: string;
  images: Map<string, Blob>;
  previewBlob?: Blob | null;
  previewFileName?: string;
  freshAssets?: Set<string>;
  isFree?: boolean;
}

interface AssetItem {
  logicalName: string;
  blob: Blob;
  contentType: AssetContentType;
}

function detectContentType(blob: Blob, fallback: AssetContentType = "image/png"): AssetContentType {
  const mime = blob.type;
  if (mime === "image/jpeg" || mime === "image/png" || mime === "image/webp") {
    return mime;
  }
  return fallback;
}

function collectAssets(input: SaveTemplateInput): AssetItem[] {
  const out: AssetItem[] = [];
  const seen = new Set<string>();
  const fresh = input.freshAssets;
  const include = (name: string) => fresh === undefined || fresh.has(name);

  if (input.previewBlob && input.previewFileName && include(input.previewFileName)) {
    out.push({
      logicalName: input.previewFileName,
      blob: input.previewBlob,
      contentType: detectContentType(input.previewBlob, "image/png"),
    });
    seen.add(input.previewFileName);
  }
  for (const layer of input.template.layers) {
    if (layer.type !== "image") continue;
    if (seen.has(layer.imageUrl)) continue;
    if (!include(layer.imageUrl)) continue;
    const blob = input.images.get(layer.imageUrl);
    if (!blob) continue;
    seen.add(layer.imageUrl);
    out.push({
      logicalName: layer.imageUrl,
      blob,
      contentType: detectContentType(blob, "image/png"),
    });
  }
  return out;
}

export function useSaveTemplate() {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<SaveStep>({ phase: "idle" });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const save = useCallback(
    async (input: SaveTemplateInput): Promise<TemplateRecord> => {
      setIsSaving(true);
      setError(null);
      try {
        // 1. Ensure template id (create if needed)
        let id = input.existingId;
        if (!id) {
          setStep({ phase: "creating" });
          const created = await templatesApi.create({
            nameTranslations: input.nameTranslations,
          });
          id = created.id;
        }

        // 2. Register + upload each asset
        const assets = collectAssets(input);
        for (let i = 0; i < assets.length; i++) {
          const asset = assets[i];
          setStep({
            phase: "uploading",
            current: i + 1,
            total: assets.length,
            fileName: asset.logicalName,
          });
          const meta = await templatesApi.requestAssetUpload(id, {
            logicalName: asset.logicalName,
            contentType: asset.contentType,
          });
          const file = new File([asset.blob], asset.logicalName, { type: asset.contentType });
          await uploadFileToSignedUrl(meta.signedUploadUrl, meta.token, file);
        }

        // 3. PUT canonical JSON
        setStep({ phase: "saving" });
        const updated = await templatesApi.update(id, {
          nameTranslations: input.nameTranslations,
          preview: input.preview,
          width: input.template.width,
          height: input.template.height,
          layers: input.template.layers,
          ...(input.isFree !== undefined ? { isFree: input.isFree } : {}),
        });

        // 4. Invalidate caches (await so the marks are committed before save resolves)
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: templateKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: templateKeys.detail(id) }),
        ]);

        setStep({ phase: "done" });
        toast({ title: "Template salvo", variant: "success" });
        return updated;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        setStep({ phase: "idle" });
        toast({
          title: "Erro ao salvar template",
          description: e.message,
          variant: "destructive",
        });
        throw e;
      } finally {
        setIsSaving(false);
      }
    },
    [queryClient],
  );

  const reset = useCallback(() => {
    setStep({ phase: "idle" });
    setError(null);
  }, []);

  return { save, step, isSaving, error, reset };
}
